
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../services/firestore';
import { collection, getDocs, getDoc, doc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { initializeApp, deleteApp } from 'firebase/app';
import { auth } from '../../firebase';
import Layout from '../../components/Layout';
import { useModal } from '../../contexts/ModalContext';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import ActionMenu from '../../ui/ActionMenu';
import Badge from '../../ui/Badge';
import Tabs from '../../ui/Tabs/Tabs';
import DataTable from '../../ui/DataTable/DataTable';
import { CellText, CellStatus, CellTag, CellMuted } from '../../ui/DataTable/cells';
import { Field, Input, Select, Textarea, FormGrid, FormFooter } from '../../ui/Field/Field';

import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import LoadingSpinner from '../../components/LoadingSpinner';
import { DEFAULT_ROLES } from '../../constants/permissions';
import { CATALOG_VERSION } from '../../constants/permissionCatalog';
import { effectivePermissions } from '../../utils/permissionOverrides';
import logger from '../../utils/logger';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions, holds } from '../../hooks/usePermissions';
import { ALL_NAV_ITEMS } from '../../ui/AppShell/navigation';
import DeleteCustomerPanel from './DeleteCustomerPanel';

import StaffCustomersPanel from './StaffCustomersPanel';
import DailyEmailPanel from './DailyEmailPanel';
import ApiReferencePanel from './ApiReferencePanel';
import ActivityLogPanel from './ActivityLogPanel';
import EngineSchedule from '../EngineSchedule/EngineSchedule';

import { useCompanySettings, saveCompanySettings } from '../../services/companySettings';
import { adminService } from '../../services/adminService';
import { finesService, errorMessage } from '../../services/finesService';
import Dialog from '../../ui/Dialog/Dialog';

/** The pages a role opens, in the menu's order - the choices for a user's start page (4 Oct 2026). */
const startPagesFor = (roleId, role, version) => (roleId && (role || roleId === 'admin')
    ? ALL_NAV_ITEMS.filter((i) => i.permission && holds({ role: roleId, permissions: role?.permissions || [], permissionsVersion: version }, i.permission))
    : []);


export default function UserManagement() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    // each control its own permission (owner's permissions plan, phase 2 - 4 Oct 2026), through usePermissions ("show
    // the app as a role" too). The user / settings powers are the Admin role's own (ADMIN_LOCKED - owner: "للـ Admin بس"):
    // no other role ever holds them, so they stay the Admin's, as the database rules require.
    const { hasPermission, isAdmin } = usePermissions();
    const canSchedules = hasPermission('manage_engine_schedule');
    // The database rules let only the Admin role create, change, lock or delete a profile (and write the
    // Force Global Refresh): manage_users alone opens the page to see the users; the writes and the password
    // help (reset link / temporary password - owner, 3 Oct 2026) are offered to the Admin role only - no more
    // "Operation Failed" after the sign-in account was already made (audit ADM-11). Manage Roles opens with
    // manage_roles.
    const canWriteUsers = hasPermission('users_edit');
    const canLockUsers = hasPermission('users_lock');
    const canDeleteUsers = hasPermission('users_delete');
    const canPasswordHelp = hasPermission('users_password_help');
    const canGlobalRefresh = hasPermission('settings_global_refresh');
    const canCompanySettings = hasPermission('settings_company');
    const canManageRoles = hasPermission('manage_roles');
    const { showConfirm, showAlert, showToast } = useModal();
    const { settings: companySettings, loading: companySettingsLoading } = useCompanySettings();
    const [companyForm, setCompanyForm] = useState(null); // populated once companySettings resolves
    const [savingCompanySettings, setSavingCompanySettings] = useState(false);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('users'); // 'users', 'system'
    const [formData, setFormData] = useState({
        uid: '', name: '', email: '', role: 'viewer', password: '', homePage: '',
        permissions: [...(DEFAULT_ROLES.viewer?.permissions || [])]
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    // Roles are still loaded here to populate the Role dropdown when creating/editing a user.
    // Role editing itself lives in the dedicated RoleManagement page (see "Manage Roles" link below).
    const [roles, setRoles] = useState(DEFAULT_ROLES);
    // the catalogue version of those roles (settings/roles.permissionsVersion; the built-in defaults are upgraded): a
    // user saved here carries it, so the app's fallback for new permissions (usePermissions) follows the role the user
    // got - before, a user saved after the upgrade had no version and got new permissions beyond a trimmed role
    const [rolesVersion, setRolesVersion] = useState(CATALOG_VERSION);
    // 'loading' | 'ready' | 'error'. Create / Update write the role's permission list onto the
    // user, so they wait for the live roles: after a failed read the list here is the code's
    // default, and a saved user got that instead of the live role (audit ADM-10).
    const [rolesStatus, setRolesStatus] = useState('loading');

    // Fetch Users - always from the database: a 24 h copy in the browser showed old roles and locks after
    // Role Management saved (audit ADM-11); the old copy is dropped.
    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try { localStorage.removeItem('YELO_USERS_CACHE'); } catch (e) { /* storage blocked: nothing to drop */ }
        try {
            const querySnapshot = await getDocs(collection(db, "users"));
            const usersList = querySnapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    ...data,
                    isLocked: data.isLocked === true || data.isLocked === 'true', // Normalize to Boolean
                    uid: doc.id,
                    id: doc.id
                };
            });
            setUsers(usersList);
        } catch (error) {
            logger.error("Error fetching users:", error);
            showToast("Failed to load users: " + error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // Fetch Roles from Firestore
    const fetchRoles = useCallback(async () => {
        logger.log('🔄 Loading roles from Firestore...');
        setRolesStatus('loading');
        try {
            const rolesRef = doc(db, 'settings', 'roles');
            const rolesDoc = await getDoc(rolesRef);
            if (rolesDoc.exists()) {
                const rolesData = rolesDoc.data();
                if (rolesData && rolesData.roles) {
                    logger.log('✅ Loaded roles:', rolesData.roles);
                    setRoles(rolesData.roles);
                    setRolesVersion(Number(rolesData.permissionsVersion) || 1);
                } else {
                    logger.log('⚠️ No roles found in document, using defaults');
                }
            } else {
                logger.log('⚠️ Roles document does not exist, using defaults');
            }
            setRolesStatus('ready');
        } catch (error) {
            logger.error("❌ Error fetching roles:", error);
            setRolesStatus('error');
            showToast('Failed to load the roles: users cannot be created or updated until they load.', 'error');
        }
    }, [showToast]);


    useEffect(() => {
        fetchUsers();
        fetchRoles();
    }, [fetchUsers, fetchRoles]);

    // Seed the editable Company Settings form once the Firestore doc (or defaults) resolves.
    // Only seeds once so it doesn't clobber values the admin is mid-way through editing.
    useEffect(() => {
        if (!companySettingsLoading && !companyForm) {
            setCompanyForm({
                fuel_price_per_liter: companySettings.fuel_price_per_liter,
                price_per_km: companySettings.price_per_km,
                trn: companySettings.trn,
                reminder_dubai_recipients: (companySettings.reminder_recipients?.dubai || []).join('\n'),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companySettingsLoading, companySettings]);

    const handleSaveCompanySettings = async () => {
        if (!companyForm) return;
        setSavingCompanySettings(true);
        try {
            const parseLines = (text) => text.split('\n').map(s => s.trim()).filter(Boolean);
            const result = await saveCompanySettings({
                fuel_price_per_liter: Number(companyForm.fuel_price_per_liter),
                price_per_km: Number(companyForm.price_per_km),
                trn: companyForm.trn,
                reminder_recipients: {
                    dubai: parseLines(companyForm.reminder_dubai_recipients),
                },
            });
            if (result.success) {
                showToast("Company settings saved!", 'success');
            } else {
                showToast("Failed to save company settings: " + (result.error?.message || 'Unknown error'), 'error');
            }
        } catch (error) {
            showToast("Failed to save company settings: " + error.message, 'error');
        } finally {
            setSavingCompanySettings(false);
        }
    };

    // User Management Functions
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!canWriteUsers) return showAlert({ title: "Not allowed", message: "Only the Admin role can create or change users.", isError: true });
        if (!isEditing && !formData.password) return showAlert({ title: "Validation Error", message: "Password is required for new users.", isError: true });
        if (!isEditing && formData.password.length < 6) return showAlert({ title: "Validation Error", message: "Password must be at least 6 characters.", isError: true });
        if (isEditing && !formData.uid) return showAlert({ title: "Validation Error", message: "User ID is missing. Please try again.", isError: true });
        if (rolesStatus !== 'ready') return showAlert({ title: "Roles not loaded", message: "The roles could not be loaded, so the user would get the built-in default permissions instead of the live role. Reload the roles and try again.", isError: true });
        // a role deleted before ADM-08 gave the user an empty list here without a word
        if (!roles[formData.role]) return showAlert({ title: "Pick a role", message: `The role "${formData.role || ''}" no longer exists. Pick one of the roles in the list.`, isError: true });

        setIsCreating(true);
        try {
            let targetUid = formData.uid;

            if (!isEditing) {
                const secondaryApp = initializeApp(auth.app.options, "SecondaryApp");
                const secondaryAuth = getAuth(secondaryApp);
                try {
                    const userCredential = await createUserWithEmailAndPassword(secondaryAuth, formData.email, formData.password);
                    targetUid = userCredential.user.uid;
                    await signOut(secondaryAuth);
                } catch (authError) {
                    await deleteApp(secondaryApp);
                    throw authError;
                }
                await deleteApp(secondaryApp);
            }

            // Roles are the only source of permissions (owner's rule, 24 Sep 2026): the user
            // gets exactly the role's list, and any personal exception is cleared on save.
            const rolePermissions = roles[formData.role]?.permissions || [];
            const permissionsToSave = [...rolePermissions];
            const overrides = { add: [], remove: [] };

            const userRef = doc(db, "users", targetUid);
            const dataToSave = {
                uid: targetUid,
                name: formData.name,
                email: formData.email,
                role: formData.role,
                permissions: permissionsToSave,
                permissionsVersion: rolesVersion,
                // the page this user opens on (4 Oct 2026); empty = the role's first page
                homePage: formData.homePage || null,
                permission_overrides: overrides,
                last_updated: new Date().toISOString()
            };

            if (!isEditing) {
                dataToSave.createdAt = new Date().toISOString();
                dataToSave.isLocked = false;
                dataToSave.security = {
                    forceChangePassword: true
                };
            }

            await setDoc(userRef, dataToSave, { merge: true });
            // the activity log: user profiles are written to Firebase, not through the engine (owner, 20 Oct 2026)
            adminService.logUserAction(isEditing ? 'UPDATE_USER_PROFILE' : 'CREATE_USER',
                `${isEditing ? 'Updated' : 'Created'} user ${formData.email} (role ${formData.role})`, currentUser,
                { source: 'web', email: formData.email, name: formData.name, role: formData.role, homePage: formData.homePage || '' });
            showToast(isEditing ? "Profile Updated!" : "User Created Successfully!", 'success');
            setFormData({ uid: '', name: '', email: '', role: 'viewer', password: '', homePage: '', permissions: [...(roles.viewer?.permissions || [])] });
            setIsEditing(false);
            fetchUsers();

        } catch (error) {
            logger.error("Operation failed:", error);
            let msg = error.message;
            if (msg.includes("email-already-in-use")) msg = "User with this email already exists.";
            showAlert({ title: "Operation Failed", message: msg, isError: true });
        } finally {
            setIsCreating(false);
        }
    };

    const handleEdit = (user) => {
        setFormData({
            uid: user.uid,
            name: user.name,
            email: user.email,
            role: user.role,
            password: '',
            homePage: user.homePage || '',
            // stored effective list; a profile saved before overrides existed starts from its role
            permissions: user.permissions || effectivePermissions(roles[user.role]?.permissions, user.permission_overrides)
        });
        setIsEditing(true);
    };

    // Changing the role in the form re-bases the ticks on the new role (keeping nothing
    // from the old one - the admin then adds this person's extras on top).
    const changeRole = (role) => setFormData((f) => ({ ...f, role, permissions: [...(roles[role]?.permissions || [])] }));

    const handleDelete = (uid) => {
        // On 13 Sep 2026 the admin profile itself was deleted here and the app rebuilt it
        // as a viewer at the next login: an admin never deletes their own profile, and every
        // profile delete is logged with the target so it can be traced.
        if (currentUser && uid === currentUser.uid) {
            showAlert({ title: "Not allowed", message: "You cannot delete your own profile.", isError: true });
            return;
        }
        const target = users.find(u => u.uid === uid);
        showConfirm({
            title: "Delete User Profile?",
            message: `This will permanently delete the profile of ${target?.email || uid}. The person keeps their login; the next time they sign in they get a new 'pending' profile with no permission until an admin gives them a role.`,
            isDangerous: true,
            confirmLabel: "Delete Forever",
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, "users", uid));
                    try { await adminService.logUserAction('DELETE_USER_PROFILE', `Deleted user profile ${target?.email || uid} (role ${target?.role || '?'})`, currentUser); } catch (e) { logger.warn('audit log failed', e); }
                    showToast("User profile deleted.", 'success');
                    fetchUsers();
                } catch (error) {
                    showToast("Error deleting: " + error.message, 'error');
                }
            }
        });
    };

    // A user who cannot sign in: the reset e-mail often never arrives. The engine (firebase-admin)
    // makes a reset link to send on WhatsApp, or sets a temporary password the user must change
    // right after signing in - no more deleting the account and creating it again.
    const [pwHelp, setPwHelp] = useState(null);      // { user, kind: 'link' | 'temp', value }
    const passwordHelp = (user, kind) => {
        showConfirm({
            title: kind === 'link' ? 'Password reset link' : 'Temporary password',
            message: kind === 'link'
                ? `Make a password-reset link for ${user.name} (${user.email})? You send it to them on WhatsApp; it works once.`
                : `Set a temporary password for ${user.name} (${user.email})? Their current password stops working, and they must choose a new one right after signing in.`,
            isDangerous: kind === 'temp',
            confirmLabel: kind === 'link' ? 'Make the link' : 'Set temporary password',
            onConfirm: async () => {
                try {
                    const r = await finesService.userPasswordHelp(user.email, kind);
                    setPwHelp({ user, kind, value: kind === 'link' ? r.link : r.password });
                } catch (err) {
                    showToast(errorMessage(err), 'error');
                }
            },
        });
    };
    const pwHelpText = pwHelp ? (pwHelp.kind === 'link'
        ? `Hello ${pwHelp.user.name}, choose a new Yelo password from this link: ${pwHelp.value}`
        : `Hello ${pwHelp.user.name}, your temporary Yelo password is ${pwHelp.value} - sign in with it and choose a new one.`) : '';

    const toggleLock = (user) => {
        const newStatus = !user.isLocked;
        showConfirm({
            title: `${newStatus ? 'LOCK' : 'UNLOCK'} Account ? `,
            message: newStatus ? `Lock ${user.name}? They will be signed out immediately.` : `Unlock ${user.name} 's account?`,
            isDangerous: newStatus,
            confirmLabel: newStatus ? "Yes, Lock Account" : "Yes, Unlock Account",
            onConfirm: async () => {
                try {
                    await updateDoc(doc(db, "users", user.uid), {
                        isLocked: !!newStatus,
                        last_updated: new Date().toISOString()
                    });
                    setUsers(users.map(u => u.uid === user.uid ? { ...u, isLocked: !!newStatus } : u));
                    adminService.logUserAction(newStatus ? 'LOCK_USER' : 'UNLOCK_USER',
                        `${newStatus ? 'Locked' : 'Unlocked'} user ${user.email}`, currentUser, { source: 'web', email: user.email });
                    showToast(`User ${newStatus ? 'LOCKED' : 'UNLOCKED'} successfully.`, 'success');
                } catch (err) {
                    showToast("Error: " + err.message, 'error');
                }
            }
        });
    };

    const resetForm = () => {
        setIsEditing(false);
        setFormData({ uid: '', name: '', email: '', role: 'viewer', password: '', homePage: '', permissions: [...(roles.viewer?.permissions || [])] });
    };

    const tabItems = [
        { id: 'users', label: 'Users', icon: <Icon glyph={ICONS.users} size="sm" /> },
        // Admins only. The engine decides this too, and refuses anyone
        // else - hiding the tab just saves them the 403.
        ...(hasPermission('customers_delete_on_ejar') ? [{ id: 'customers', label: 'Delete customers', icon: <Icon glyph={ICONS.users} size="sm" /> }] : []),
        // the staff who pay no Salik / Darb trip fee (owner, 1 Oct 2026) - admins only, the engine checks it too
        ...(hasPermission('manage_staff_on_ejar') ? [{ id: 'staff', label: 'Staff on Ejar', icon: <Icon glyph={ICONS.salik} size="sm" /> }] : []),
        ...(canSchedules ? [{ id: 'schedules', label: 'Schedules', icon: <Icon glyph={ICONS.clock} size="sm" /> }] : []),
        // who gets the daily report e-mail (owner, 5 Oct 2026) - admins only, the engine checks it too
        ...(isAdmin ? [{ id: 'email', label: 'E-mail report', icon: <Icon glyph={ICONS.settings} size="sm" /> }] : []),
        // the engine's API reference: admins only (the engine refuses anyone else too)
        ...(hasPermission('view_api_reference') ? [{ id: 'api', label: 'API reference', icon: <Icon glyph={ICONS.engine} size="sm" /> }] : []),
        // everything every user did (owner, 20 Oct 2026: "ضمن صفحه الادمن مع صلاحيات الادمن بس") - the engine refuses anyone else
        ...(isAdmin ? [{ id: 'activity', label: 'Activity log', icon: <Icon glyph={ICONS.list} size="sm" /> }] : []),
        { id: 'system', label: 'System', icon: <Icon glyph={ICONS.settings} size="sm" /> },
    ];

    const userColumns = [
        {
            key: 'name',
            header: 'User',
            cell: (user) => (
                <div className="flex flex-col items-center">
                    <CellText>{user.name}</CellText>
                    <CellMuted>{user.email}</CellMuted>
                </div>
            ),
            searchValue: (user) => `${user.name || ''} ${user.email || ''}`,
        },
        {
            key: 'status',
            header: 'Status',
            accessor: (user) => (user.isLocked ? 'Locked' : 'Active'),
            cell: (user) => <CellStatus status={user.isLocked ? 'locked' : 'active'} label={user.isLocked ? 'Locked' : 'Active'} />,
        },
        {
            key: 'role',
            header: 'Role',
            cell: (user) => (
                <div className="flex flex-wrap items-center justify-center gap-1">
                    <CellTag>{user.role}</CellTag>
                    {((user.permission_overrides?.add?.length || 0) + (user.permission_overrides?.remove?.length || 0)) > 0 && (
                        <Badge variant="warning" size="sm" title="Still carries personal exceptions from before roles became the only source; Edit then Update clears them">exceptions</Badge>
                    )}
                </div>
            ),
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            sortable: false,
            searchable: false,
            exportValue: () => '',
            cell: (user) => (
                // Clicks inside the menu must not reach the row, like the old buttons' stopPropagation
                <div className="flex justify-center items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    {canWriteUsers && <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); handleEdit(user); }}>Edit</Button>}
                    <ActionMenu
                        iconOnly
                        icon={ICONS.more}
                        label="More actions"
                        variant="ghost"
                        size="xs"
                        width={200}
                        items={[
                            ...(canPasswordHelp ? [
                                {
                                    label: 'Password reset link',
                                    icon: ICONS.key,
                                    onClick: () => passwordHelp(user, 'link'),
                                },
                                {
                                    label: 'Temporary password',
                                    icon: ICONS.lock,
                                    onClick: () => passwordHelp(user, 'temp'),
                                },
                            ] : []),
                            ...(canLockUsers ? [{
                                label: user.isLocked ? 'Unlock' : 'Lock',
                                icon: user.isLocked ? ICONS.unlock : ICONS.lock,
                                onClick: () => toggleLock(user),
                            }] : []),
                            ...(canDeleteUsers ? [
                                { divider: true },
                                {
                                    label: 'Delete',
                                    icon: ICONS.delete,
                                    danger: true,
                                    onClick: () => handleDelete(user.uid),
                                },
                            ] : []),
                        ]}
                    />
                </div>
            ),
        },
    ];

    return (
        <Layout
            title="Administration"
            subtitle="Everything that belongs to the owner"
            actions={canManageRoles ? (
                <Button variant="secondary" size="sm" onClick={() => navigate('/admin/roles')} leadingIcon={<Icon glyph={ICONS.settings} size="sm" />}>
                    Manage Roles
                </Button>
            ) : null}
        >
            <div className="w-full max-w-7xl mx-auto">
                <Tabs
                    className="mb-6"
                    idPrefix="admin"
                    label="Administration sections"
                    items={tabItems}
                    value={activeTab}
                    onChange={setActiveTab}
                />

                <div role="tabpanel" id={`admin-panel-${activeTab}`} aria-labelledby={`admin-${activeTab}`}>
                {activeTab === 'schedules' ? <EngineSchedule />
                    : activeTab === 'customers' ? <DeleteCustomerPanel />
                    : activeTab === 'staff' ? <StaffCustomersPanel />
                    : activeTab === 'email' ? <DailyEmailPanel />
                    : activeTab === 'api' ? <ApiReferencePanel />
                    : activeTab === 'activity' ? <ActivityLogPanel />
                    : activeTab === 'users' ? (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {canWriteUsers ? (
                        <div className="lg:col-span-1">
                            <Card className="lg:sticky lg:top-6" title={isEditing ? 'Edit User' : 'Add New User'}>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <Field label="Full Name" required>
                                        <Input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                                    </Field>
                                    <Field label="Email" required>
                                        <Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required disabled={isEditing} />
                                    </Field>
                                    {!isEditing && (
                                        <Field label="Password" required help="At least 6 characters.">
                                            <Input type="text" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} required minLength={6} />
                                        </Field>
                                    )}
                                    <Field label="Role" help="The role is the only source of permissions. Change a role in Manage Roles and every user of that role follows.">
                                        <Select value={formData.role} onChange={e => changeRole(e.target.value)}>
                                            {/* a user whose role was deleted (e.g. 'parking'): shown as it is, not as the first role in the list */}
                                            {formData.role && !roles[formData.role] && (
                                                <option value={formData.role} disabled>{formData.role} (role no longer exists - pick one)</option>
                                            )}
                                            {Object.entries(roles).map(([id, r]) => <option key={id} value={id}>{r.name}</option>)}
                                        </Select>
                                    </Field>
                                    <Field label="Start page" help="The page this user opens on after signing in. Only the pages the role opens are listed.">
                                        <Select value={formData.homePage || ''} onChange={e => setFormData({ ...formData, homePage: e.target.value })}>
                                            <option value="">The role's first page (automatic)</option>
                                            {startPagesFor(formData.role, roles[formData.role], rolesVersion)
                                                .map((i) => <option key={i.path} value={i.path}>{i.group ? `${i.group} · ${i.label}` : i.label}</option>)}
                                            {formData.homePage && !startPagesFor(formData.role, roles[formData.role], rolesVersion).some((i) => i.path === formData.homePage) && (
                                                <option value={formData.homePage} disabled>{formData.homePage} (this role cannot open it - the role's first page is used)</option>
                                            )}
                                        </Select>
                                    </Field>
                                    {rolesStatus === 'error' && (
                                        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                                            The roles could not be loaded, so users cannot be created or updated.{' '}
                                            <Button type="button" size="xs" variant="secondary" onClick={fetchRoles}>Retry</Button>
                                        </div>
                                    )}
                                    <FormFooter sticky={false}>
                                        {isEditing && <Button type="button" variant="secondary" onClick={resetForm}>Cancel</Button>}
                                        <Button type="submit" variant="primary" isLoading={isCreating} disabled={isCreating || rolesStatus !== 'ready'}>
                                            {isEditing ? 'Update' : 'Create'}
                                        </Button>
                                    </FormFooter>
                                </form>
                            </Card>
                        </div>
                        ) : (
                        <div className="lg:col-span-1">
                            <Card title="Users">
                                <p className="text-sm text-gray-700" role="status">
                                    Only the Admin role can create, change, lock or delete users. You can see the list and make a password reset link or a temporary password.
                                </p>
                            </Card>
                        </div>
                        )}
                        <div className="lg:col-span-2">
                            <DataTable
                                title={`System Users (${users.length})`}
                                headerActions={(
                                    <Button size="sm" variant="secondary" onClick={() => fetchUsers()} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Refresh</Button>
                                )}
                                columns={userColumns}
                                data={users}
                                rowKey="uid"
                                loading={loading}
                                searchPlaceholder="Search users…"
                                emptyTitle="No users yet"
                                emptyDescription="Create the first user with the form."
                                emptyIcon={ICONS.users}
                            />
                        </div>

                        {/* Roles are the only source of permissions: this card just says what the chosen role grants */}
                        <div className="lg:col-span-3">
                            <Card
                                title="Permissions come from the role"
                                subtitle={`${roles[formData.role]?.name || formData.role} · ${(roles[formData.role]?.permissions || []).length} permissions`}
                                actions={canManageRoles ? <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/admin/roles')}>Manage Roles</Button> : null}
                            >
                                <p className="text-sm text-gray-700">
                                    Pick the role above; the user gets exactly what that role grants. To change what a role grants, edit the role in Manage Roles - every user of that role follows.
                                </p>
                            </Card>
                        </div>
                    </div>
                ) : (
                    <div className="max-w-2xl mx-auto">
                        {canGlobalRefresh ? (
                        <Card title="System Control">
                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                <h4 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                                    <Icon glyph={ICONS.warning} size="sm" tone="warning" />
                                    Force Global Refresh
                                </h4>
                                <p className="text-sm text-gray-700 mb-4">This will force all users to reload and clear caches.</p>
                                <Button
                                    variant="danger"
                                    leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}
                                    onClick={() => {
                                        showConfirm({
                                            title: "Force Refresh?",
                                            message: "All users will be interrupted. Continue?",
                                            isDangerous: true,
                                                    onConfirm: async () => {
                                                        try {
                                                            const ref = doc(db, 'settings', 'maintenance');
                                                            await setDoc(ref, {
                                                                refreshTrigger: Date.now().toString(),
                                                                last_updated: new Date().toISOString()
                                                            }, { merge: true });
                                                            adminService.logUserAction('GLOBAL_REFRESH', 'Forced every user to reload', currentUser, { source: 'web' });
                                                            showToast("Refresh triggered!", 'success');
                                                        } catch (e) { showToast(e.message, 'error'); }
                                                    }
                                        });
                                    }}
                                >
                                    Execute Global Refresh (F5)
                                </Button>
                            </div>
                        </Card>
                        ) : (
                        <Card title="System Control">
                            <p className="text-sm text-gray-700" role="status">Force Global Refresh is for the Admin role only.</p>
                        </Card>
                        )}

                        {canCompanySettings && (
                            <Card className="mt-6" title="Company Settings">
                                {!companyForm ? (
                                    <LoadingSpinner message="Loading settings..." />
                                ) : (
                                    <div className="space-y-4">
                                        <FormGrid cols={2}>
                                            <Field label="Fuel Price (per liter)">
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={companyForm.fuel_price_per_liter}
                                                    onChange={e => setCompanyForm({ ...companyForm, fuel_price_per_liter: e.target.value })}
                                                />
                                            </Field>
                                            <Field label="Price per KM (mileage)">
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={companyForm.price_per_km}
                                                    onChange={e => setCompanyForm({ ...companyForm, price_per_km: e.target.value })}
                                                />
                                            </Field>
                                            <Field label="Company TRN" help="Printed on every tax invoice Yelo makes (Corporate, Sales, Fuel, Mileage, Monthly).">
                                                <Input
                                                    type="text"
                                                    value={companyForm.trn}
                                                    onChange={e => setCompanyForm({ ...companyForm, trn: e.target.value })}
                                                />
                                            </Field>
                                        </FormGrid>
                                        {/* The fine service fee (5%), the VAT on it and the AED 20 knowledge fee are set in the engine's
                                            fines config (FINES_SERVICE_FEE_PCT / FINES_FEE_VAT_PCT / FINES_IMPOUND_KNOWLEDGE_FEE) - the three
                                            fields that were here were read by nothing (audit DUP-15). */}

                                        <Field label="Collections (day 13) e-mail recipients — Dubai airport" help="One e-mail address per line.">
                                            <Textarea
                                                rows={4}
                                                className="font-mono text-sm"
                                                value={companyForm.reminder_dubai_recipients}
                                                onChange={e => setCompanyForm({ ...companyForm, reminder_dubai_recipients: e.target.value })}
                                            />
                                        </Field>

                                        <FormFooter sticky={false}>
                                            <Button
                                                variant="primary"
                                                onClick={handleSaveCompanySettings}
                                                disabled={savingCompanySettings}
                                                isLoading={savingCompanySettings}
                                            >
                                                Save Company Settings
                                            </Button>
                                        </FormFooter>
                                    </div>
                                )}
                            </Card>
                        )}
                    </div>
                )}
                </div>
            </div>
            <Dialog
                open={!!pwHelp}
                onClose={() => setPwHelp(null)}
                size="sm"
                title={pwHelp?.kind === 'link' ? 'Reset link ready' : 'Temporary password set'}
                description={pwHelp ? `${pwHelp.user.name} · ${pwHelp.user.email}` : ''}
                footer={pwHelp && (
                    <>
                        <Button onClick={() => { navigator.clipboard?.writeText(pwHelpText); showToast('Copied - paste it to the user', 'success'); }}
                            leadingIcon={<Icon glyph={ICONS.copy} size="sm" />}>Copy message</Button>
                        <a className="yelo-btn yelo-btn--primary yelo-btn--md" target="_blank" rel="noopener noreferrer"
                            href={`https://wa.me/?text=${encodeURIComponent(pwHelpText)}`}>
                            <span className="yelo-btn__label">Send on WhatsApp</span>
                        </a>
                    </>
                )}
            >
                {pwHelp && (
                    <div className="flex flex-col gap-3 text-sm">
                        <div className="font-mono break-all rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-gray-900 select-all">{pwHelp.value}</div>
                        <p className="text-gray-600">
                            {pwHelp.kind === 'link'
                                ? 'The link opens a page where the user types a new password. It works once and expires after a while.'
                                : 'The user signs in with this password; yelo then asks them to choose their own before anything else.'}
                        </p>
                    </div>
                )}
            </Dialog>
        </Layout>
    );
}
